import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { DishDto, MealRuleDish, SubscriptionMealRules } from '@tiffinify/contracts';
import { mealsOf, tiffinName, tiffinsOf, type Food, type Meal, type Slot, type Tiffin } from '@tiffinify/core';
import { mealName } from '../lib/format';
import { Badge, BtnPrimary, Card, DietChips, DietPicker, EmptyFilterState, Field, FoodMark, Helper, Icon, MealIcon, SearchField, Section, Sheet, type IconName } from './ui';

export interface MealChoiceProps {
  slot: Slot;
  mealRules: SubscriptionMealRules;
  onChangeMealRules: (rules: SubscriptionMealRules) => void;
  dishes: DishDto[];
  customerFood?: Food;
  /** The customer's diet follows their first tiffin. */
  onChangeCustomerFood?: (food: Food) => void;
  notes?: string;
  onChangeNotes?: (notes: string) => void;
  title?: string;
  helperText?: string;
}

/** One entry per tiffin, so each can be changed on its own (older saves kept "3 of the same" as one entry). */
function tiffinsFor(rules: SubscriptionMealRules, meal: Meal, food: Food): Tiffin[] {
  return tiffinsOf(rules[meal], food).flatMap((t) =>
    Array.from({ length: t.quantity }, (_, i) => ({ ...t, id: t.quantity > 1 ? `${t.id}-${i + 1}` : t.id, quantity: 1 })));
}

/** What each meal of the plan brings: one or more tiffins, each the day's special in a diet or chosen dishes. */
export function MealChoiceSection({
  slot, mealRules, onChangeMealRules, dishes, customerFood = 'veg', onChangeCustomerFood, notes, onChangeNotes, title = 'Meal choices', helperText,
}: MealChoiceProps) {
  const [picking, setPicking] = useState<{ meal: Meal; index: number } | null>(null);

  const save = (m: Meal, tiffins: Tiffin[]) => onChangeMealRules({
    ...mealRules,
    [m]: { mode: 'portions', portions: tiffins.map((t) => ({ id: t.id, food: t.food, mode: t.dishes.length ? 'dishes' : 'special', dishes: t.dishes.length ? t.dishes : undefined, quantity: 1 })) },
  });
  const change = (m: Meal, index: number, patch: Pick<Tiffin, 'food' | 'dishes'>) => {
    const tiffins = tiffinsFor(mealRules, m, customerFood);
    const t = tiffins[index];
    if (!t) return;
    tiffins[index] = { ...t, ...patch };
    if (index === 0 && patch.food !== t.food) onChangeCustomerFood?.(patch.food);
    save(m, tiffins);
  };
  const add = (m: Meal) => {
    const tiffins = tiffinsFor(mealRules, m, customerFood);
    // A second tiffin is usually for someone else at home: offer the other main diet.
    const food: Food = tiffins.length === 1 ? (tiffins[0]!.food === 'veg' ? 'nonveg' : 'veg') : customerFood;
    save(m, [...tiffins, { id: `p-${tiffins.length + 1}-${Date.now().toString(36)}`, food, dishes: [], quantity: 1 }]);
  };
  const remove = (m: Meal, index: number) => save(m, tiffinsFor(mealRules, m, customerFood).filter((_, i) => i !== index));

  const target = picking ? tiffinsFor(mealRules, picking.meal, customerFood)[picking.index] : undefined;

  return (
    <Section title={title}>
      <Card pad>
        {helperText && <Helper>{helperText}</Helper>}
        {mealsOf(slot).map((m) => {
          const tiffins = tiffinsFor(mealRules, m, customerFood);
          const many = tiffins.length > 1;
          return (
            <div key={m} className="meal-choice">
              <div className="meal-choice-head">
                <MealIcon meal={m} size={16} />
                <b>{mealName(m)}</b>
                {many && <Badge kind="saffron">{tiffins.length} tiffins</Badge>}
              </div>
              {tiffins.map((t, i) => {
                const editor = (
                  <TiffinEditor
                    tiffin={t}
                    name={many ? `Tiffin ${i + 1}` : mealName(m)}
                    onDiet={(food) => change(m, i, { food, dishes: t.dishes.filter((d) => d.food === food) })}
                    onPick={() => setPicking({ meal: m, index: i })}
                    onSpecial={() => change(m, i, { food: t.food, dishes: [] })}
                  />
                );
                return many ? (
                  <div key={t.id} className="tiffin-card">
                    <div className="between">
                      <span className="row">
                        <span className="tiffin-badge">Tiffin {i + 1}</span>
                        <span className="tiffin-diet">{DIET_WORD[t.food]}</span>
                      </span>
                      <button type="button" className="tiffin-remove-btn" aria-label={`Remove tiffin ${i + 1}`} onClick={() => remove(m, i)}>
                        <Icon name="close" size={13} sw={2.4} />Remove
                      </button>
                    </div>
                    {editor}
                  </div>
                ) : <div key={t.id} className="stack">{editor}</div>;
              })}
              <button type="button" className="btn-add-tiffin" onClick={() => add(m)}>
                <Icon name="plus" size={14} sw={2.5} />Add another tiffin for {m}
              </button>
            </div>
          );
        })}
        {onChangeNotes && (
          <div className="meal-choice-notes">
            <Field label="Diet notes" id="f-notes" value={notes ?? ''} onChange={onChangeNotes} placeholder="e.g. no onion, extra protein, less spicy" />
          </div>
        )}
      </Card>

      {picking && target && (
        <DishPicker
          meal={picking.meal}
          name={tiffinsFor(mealRules, picking.meal, customerFood).length > 1 ? `tiffin ${picking.index + 1}` : picking.meal}
          dishes={dishes}
          food={target.food}
          chosen={target.dishes}
          onSave={(chosen) => change(picking.meal, picking.index, { food: chosen[0]?.food ?? target.food, dishes: chosen })}
          onClose={() => setPicking(null)}
        />
      )}
    </Section>
  );
}

const DIET_WORD: Record<Food, string> = { veg: 'Vegetarian', nonveg: 'Non-veg', egg: 'Egg' };

/** No dishes: the chef's special. One dish: the same dish every meal day. Several: a pool, one of them each day. */
type Choice = 'special' | 'fixed' | 'pool';
const choiceOf = (n: number): Choice => (n === 0 ? 'special' : n === 1 ? 'fixed' : 'pool');
const CHOICE: Record<Choice, { icon: IconName; text: (n: number) => string }> = {
  special: { icon: 'sparkle', text: () => 'Chef’s special · fresh daily menu' },
  fixed: { icon: 'check', text: () => 'Fixed dish · every meal day' },
  pool: { icon: 'refresh', text: (n) => `Daily rotation among ${n} dishes` },
};

function TiffinEditor({ tiffin, name, onDiet, onPick, onSpecial }: {
  tiffin: Tiffin; name: string; onDiet: (food: Food) => void; onPick: () => void; onSpecial: () => void;
}) {
  const n = tiffin.dishes.length;
  const choice = choiceOf(n);
  return (
    <>
      <div className="stack">
        <span className="label">Food preference</span>
        <DietPicker label={`${name} food preference`} value={tiffin.food} onChange={onDiet} />
      </div>
      <div className="stack">
        <span className="label">Dish selection</span>
        <div className={`tiffin-dish-box ${choice}`}>
          <FoodMark food={tiffin.food} plate />
          <span className="grow">
            <b className="tiffin-dish-name">{tiffinName(tiffin)}</b>
            <span className={`tiffin-mode ${choice}`}><Icon name={CHOICE[choice].icon} size={12} sw={2.6} />{CHOICE[choice].text(n)}</span>
          </span>
          <span className="tiffin-dish-actions">
            <button type="button" className="tiffin-pick-btn" onClick={onPick}>
              <Icon name={n ? 'edit' : 'plus'} size={13} sw={2.4} />{n ? 'Change dishes' : 'Pick dishes'}
            </button>
            {n > 0 && <button type="button" className="tiffin-reset-link" onClick={onSpecial}>Reset to chef’s special</button>}
          </span>
        </div>
      </div>
    </>
  );
}

const asRuleDish = (d: DishDto): MealRuleDish => ({ dishId: d.id, dishName: d.name, food: d.food, category: d.category, calories: d.calories, protein: d.protein });

/** Pick one dish (sent every day) or several (one of them each day) from the kitchen's dishes. */
function DishPicker({ meal, name, dishes, food, chosen, onSave, onClose }: {
  meal: Meal; name: string; dishes: DishDto[]; food: Food; chosen: MealRuleDish[]; onSave: (dishes: MealRuleDish[]) => void; onClose: () => void;
}) {
  // Older saves may lack the dish id: find the dish by name, so it shows as picked and is never added twice.
  const [picked, setPicked] = useState(() => new Map(chosen.map((d) => [d.dishId ?? dishes.find((x) => x.name === d.dishName)?.id ?? d.dishName, d])));
  const nav = useNavigate();
  const [search, setSearch] = useState('');
  const [diet, setDiet] = useState<Food | 'all'>(food);
  // What was already picked comes first, so changing a choice starts from it.
  const [before] = useState(() => new Set(picked.keys()));
  const q = search.trim().toLowerCase();
  const shown = dishes
    .filter((d) => (diet === 'all' || d.food === diet) && (!q || d.name.toLowerCase().includes(q) || d.category.toLowerCase().includes(q)))
    .sort((a, b) => Number(before.has(b.id)) - Number(before.has(a.id)));
  const toggle = (d: DishDto) => setPicked((m) => {
    const n = new Map(m);
    if (n.has(d.id)) n.delete(d.id); else n.set(d.id, asRuleDish(d));
    return n;
  });

  return (
    <Sheet kicker={mealName(meal)} title={`Pick dishes for ${name}`} sub={`${shown.length} of ${dishes.length} dishes`} onClose={onClose} className="tall">
      <div className="sheet-toolbar">
        <SearchField value={search} onChange={setSearch} label="Search dishes" placeholder="Search dishes" />
        <DietChips items={dishes} value={diet} onChange={setDiet} />
      </div>
      <div className="sheet-scroll">
        {shown.length === 0 ? (
          <EmptyFilterState
            title={dishes.length === 0 ? 'Your dish list is empty' : 'No dishes match'}
            desc={dishes.length === 0 ? 'Add the dishes you cook in Settings → Dishes, then pick them here.' : q ? `Nothing called “${search.trim()}”.` : 'Try another diet.'}
            onReset={dishes.length === 0 ? () => nav('/settings') : q || diet !== 'all' ? () => { setSearch(''); setDiet('all'); } : undefined}
            resetLabel={dishes.length === 0 ? 'Open Settings' : undefined}
          />
        ) : shown.map((d) => {
          const on = picked.has(d.id);
          return (
            <button key={d.id} type="button" className={`dish-card selectable${on ? ' selected' : ''}`} aria-pressed={on} onClick={() => toggle(d)}>
              <FoodMark food={d.food} />
              <span className="dish-card-name">{d.name}</span>
              {d.category && !/^meals?$/i.test(d.category) && <Badge>{d.category}</Badge>}
              <span className="dish-card-pick" aria-hidden="true"><Icon name={on ? 'check' : 'plus'} size={14} sw={2.8} /></span>
            </button>
          );
        })}
      </div>
      <div className="sheet-footer">
        <span className="dish-picker-status">
          <span className={`tiffin-mode ${choiceOf(picked.size)}`}>
            <Icon name={CHOICE[choiceOf(picked.size)].icon} size={12} sw={2.6} />
            {picked.size === 0 ? 'Pick 1 for fixed, or several to rotate' : picked.size === 1 ? '1 picked · fixed every meal day' : `${picked.size} picked · rotates daily`}
          </span>
          {picked.size > 0 && <button type="button" className="tiffin-reset-link" onClick={() => { onSave([]); onClose(); }}>Reset to chef’s special</button>}
        </span>
        <BtnPrimary label={picked.size ? `Done (${picked.size})` : 'Done'} icon="check" onClick={() => { onSave([...picked.values()]); onClose(); }} />
      </div>
    </Sheet>
  );
}
